/**
 * DebugProfiler - Профайлер для React компонентів
 *
 * Вимірює час рендерингу компонентів та допомагає знайти вузькі місця продуктивності
 */

import { observer } from 'mobx-react-lite'
import React, { Profiler } from 'react'

interface ProfileData {
    id: string
    phase: 'mount' | 'update' | 'nested-update'
    actualDuration: number
    baseDuration: number
    startTime: number
    commitTime: number
    interactions: Set<any>
}

interface DebugProfilerProps {
    id: string
    children: React.ReactNode
    enabled?: boolean
    onRender?: (data: ProfileData) => void
}

// Глобальное хранилище данных профилирования
const profileData: Map<string, ProfileData[]> = new Map()

export const DebugProfiler: React.FC<DebugProfilerProps> = observer(
    ({ id, children, enabled = true, onRender }: DebugProfilerProps) => {
        const handleRender: React.ProfilerOnRenderCallback = (
            id,
            phase,
            actualDuration,
            baseDuration,
            startTime,
            commitTime
        ) => {
            const data: ProfileData = {
                id,
                phase,
                actualDuration,
                baseDuration,
                startTime,
                commitTime,
                interactions: new Set(),
            }

            // Зберігаємо дані
            if (!profileData.has(id)) {
                profileData.set(id, [])
            }
            const componentData = profileData.get(id)!
            componentData.push(data)

            // Обмежуємо кількість записів
            if (componentData.length > 100) {
                componentData.shift()
            }

            // Викликаємо callback якщо надано
            if (onRender) {
                onRender(data)
            }

            // Логуємо повільні рендери
            if (actualDuration > 16) {
                // Більше 16ms вважається повільним
                console.warn(`[DebugProfiler] Повільний рендер компонента "${id}":`, {
                    phase,
                    actualDuration: `${actualDuration.toFixed(2)}ms`,
                    baseDuration: `${baseDuration.toFixed(2)}ms`,
                })
            }
        }

        if (!enabled) {
            return <>{children}</>
        }

        return (
            <Profiler id={id} onRender={handleRender}>
                {children}
            </Profiler>
        )
    }
)

/**
 * Хук для отримання даних профілювання компонента
 */
export const useProfileData = (id: string): ProfileData[] => {
    return profileData.get(id) || []
}

/**
 * Функція для отримання всіх даних профілювання
 */
export const getAllProfileData = (): Record<string, ProfileData[]> => {
    const result: Record<string, ProfileData[]> = {}
    profileData.forEach((data, id) => {
        result[id] = data
    })
    return result
}

/**
 * Функція для очищення даних профілювання
 */
export const clearProfileData = (id?: string): void => {
    if (id) {
        profileData.delete(id)
    } else {
        profileData.clear()
    }
}

/**
 * Функція для отримання статистики по компоненту
 */
export const getProfileStats = (id: string) => {
    const data = profileData.get(id) || []

    if (data.length === 0) {
        return null
    }

    const actualDurations = data.map((d) => d.actualDuration)
    const baseDurations = data.map((d) => d.baseDuration)

    const avgActual = actualDurations.reduce((a, b) => a + b, 0) / actualDurations.length
    const avgBase = baseDurations.reduce((a, b) => a + b, 0) / baseDurations.length
    const maxActual = Math.max(...actualDurations)
    const minActual = Math.min(...actualDurations)

    const mountCount = data.filter((d) => d.phase === 'mount').length
    const updateCount = data.filter((d) => d.phase === 'update').length

    return {
        totalRenders: data.length,
        mountCount,
        updateCount,
        avgActualDuration: avgActual,
        avgBaseDuration: avgBase,
        maxActualDuration: maxActual,
        minActualDuration: minActual,
        slowRenders: data.filter((d) => d.actualDuration > 16).length,
    }
}

export default DebugProfiler
